"use client";

import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ActionButton } from "@/components/access/Guard";
import { DetailsSectionCreate } from "./details-section-create";
import { useAppointmentCreateModal } from "@/contexts/AppointmentCreateModalContext";
import { useSaveAppointmentModalRecord } from "@/hooks/modal/useSaveAppointmentModalRecord";
import { externalBusyKey } from "@/hooks/calendar/useExternalBusy";
import { useQueryClient } from "@tanstack/react-query";
import { startOfWeek, formatISO } from "date-fns";
import { useState } from "react";
import {
  validateAppointmentCreate,
  hasErrors,
  type AppointmentCreateErrors,
} from "@/lib/validation/appointmentCreate";

export function ModalCreateAppointment() {
  const { t } = useTranslation();
  const {
    open,
    appointment,
    closeModal,
  } = useAppointmentCreateModal();

  // Handler for creating the appointment
  const queryClient = useQueryClient();
  const { mutate: saveAppointment, isPending: isSaving } = useSaveAppointmentModalRecord();
  const [createErrors, setCreateErrors] = useState<AppointmentCreateErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleCreate = () => {
    console.log("handleCreate called");
    console.log("Current appointment:", appointment);
    if (!appointment) return;
    // clear any prior server-side error before re-submitting
    setSaveError(null);
    // 1) validate and surface inline errors
    const errs = validateAppointmentCreate(appointment);
    console.log("Validation errors:", errs);
    setCreateErrors(errs);
    if (hasErrors(errs)) {
      // stop here; the Details section will show inline messages
      return;
    }
    saveAppointment(appointment, {
      onSuccess: (saved) => {
        // Calculate the start of the week (Monday) for week-based query keys
        const mondayISO = formatISO(
          startOfWeek(new Date(saved.date), { weekStartsOn: 1 }),
          { representation: "date" }
        );

        const weekAppsKey = ["weekAppointments", mondayISO];
        queryClient.invalidateQueries({ queryKey: weekAppsKey });
        // the week's busy bands refetch with its appointments (a 304 unless a block changed)
        queryClient.invalidateQueries({ queryKey: externalBusyKey(mondayISO) });

        closeModal();
      },
      onError: (error: any) => {
        console.error("Failed to save appointment:", error);
        // Surface the backend message (e.g. "At least one service is required",
        // "Selected time is no longer available") and keep the modal open so the
        // user can correct the appointment and retry.
        const message =
          error?.response?.data?.error ??   // our { status, error } shape
          error?.response?.data?.detail ??  // FastAPI HTTPException shape
          t("appointment.create.save_failed", "Could not create appointment. Please try again.");
        setSaveError(message);
      },
    });
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={closeModal}>
      <DialogContent className="max-w-[900px] p-0 overflow-hidden rounded-lg">
        <DialogTitle>
          <span className="sr-only">{t("appointment.create.title", "Create appointment")}</span>
        </DialogTitle>
        <DialogDescription>
          <span className="sr-only">{t("appointment.create.description", "Creating a new appointment")}</span>
        </DialogDescription>
        <div className="flex flex-col h-[80vh]">
          <div className="flex-1 overflow-y-auto">
            <DetailsSectionCreate
              errors={createErrors}
              onClearError={(field) =>
                setCreateErrors((prev) => ({ ...prev, [field]: undefined })) // Fixed: was { .prev, [field]: undefined }
              }
            />
          </div>
          <div className="border-t p-4 flex items-center justify-end gap-2">
            {saveError && (
              <p className="mr-auto text-xs text-red-600" role="alert">
                {saveError}
              </p>
            )}
            <Button variant="outline" onClick={closeModal}>
              {t("common.cancel", "Cancel")}
            </Button>
            <ActionButton
              check={(p) => p.records.create}
              reason={t("appointment.create.no_permission", "Not enough permissions to create appointment")}
              onClick={handleCreate}
              className="text-xs bg-amber-500 hover:bg-amber-600"
            >
              {isSaving ? t("common.saving", "Saving...") : t("common.create", "Create")}
            </ActionButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
